import { useModal } from "../lib/useModal";
import { useEffect, useState } from "react";

import { useRemoveCluster } from "../api/hooks";
import type { Cluster, Finding } from "../api/types";

/**
 * A theme, opened.
 *
 * The map answers how big a theme is and where it sits; this answers what is
 * in it. The entries are listed by score, because that is the order one wants
 * to read them in, and each is a way into the entry itself.
 */
export function ClusterDrawer({
  cluster,
  entries,
  onClose,
  onOpenEntry,
}: {
  cluster: Cluster | null;
  entries: Finding[];
  onClose: () => void;
  onOpenEntry: (id: string) => void;
}) {
  const remove = useRemoveCluster();
  const [confirming, setConfirming] = useState(false);
  useEffect(() => setConfirming(false), [cluster?.slug]);
  const open = cluster !== null;
  const modal = useModal(open, onClose);

  const sorted = [...entries].sort(
    (one, two) => (two.relevance_score ?? -1) - (one.relevance_score ?? -1),
  );

  return (
    <>
      <div className={"drawer-backdrop" + (open ? " open" : "")} onClick={onClose} />
      <div className={"drawer-panel" + (open ? " open" : "")} ref={modal} role="dialog" aria-modal={open ? true : undefined} aria-labelledby="cluster-drawer-title" tabIndex={-1} inert={!open}>
        {cluster && (
          <>
            <div className="drawer-head">
              <div>
                <h2 id="cluster-drawer-title">{cluster.name}</h2>
                <div className="sub mono" style={{ color: "var(--text-muted)", fontSize: 11 }}>
                  {cluster.count} {cluster.count === 1 ? "Eintrag" : "Einträge"}
                </div>
              </div>
              <button className="drawer-close" aria-label="Details schließen" onClick={onClose}>
                ×
              </button>
            </div>

            {cluster.description && (
              <div className="drawer-section">
                <h3>Worum es geht</h3>
                <p style={{ fontSize: 12.5 }}>{cluster.description}</p>
              </div>
            )}

            <div className="drawer-section">
              <h3>Einträge ({sorted.length})</h3>
              {sorted.length === 0 && (
                <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  Noch keine Einträge.
                </p>
              )}
              {sorted.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className="cluster-entry"
                  onClick={() => onOpenEntry(entry.id)}
                >
                  <span className="cluster-entry-title">
                    {entry.title}
                    {(entry.kind || entry.year) && (
                      <span className="cluster-entry-kind">
                        {[entry.kind, entry.year].filter(Boolean).join(" ")}
                      </span>
                    )}
                  </span>
                  {entry.relevance_score != null && (
                    <span className="cluster-entry-score">{entry.relevance_score}</span>
                  )}
                </button>
              ))}
            </div>

            <div className="drawer-section">
              <h3>Cluster entfernen</h3>
              <div className="action-bar" style={{ justifyContent: "flex-start" }}>
                {confirming ? (
                  <>
                    <button
                      className="btn danger"
                      disabled={remove.isPending}
                      onClick={() => {
                        remove.mutate({ slug: cluster.slug });
                        onClose();
                      }}
                    >
                      Wirklich entfernen
                    </button>
                    <button className="btn secondary" onClick={() => setConfirming(false)}>
                      Abbrechen
                    </button>
                  </>
                ) : (
                  <button className="btn danger" onClick={() => setConfirming(true)}>
                    Entfernen
                  </button>
                )}
              </div>
              <p style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 8 }}>
                Einträge bleiben erhalten, ohne Cluster-Zuordnung.
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
}
